/**
 * indoor_combat.ts — THE INDOOR RELATION (the interiors programme, slice K "indoor combat", 2026-10-02).
 *
 * BYTE-IDENTICAL in `client/src/lib/` and `server/src/lib/` (`verify:indoor-combat` compares the two files) and
 * IMPORT-FREE: the shapes below are structural — the room plan both sides keep (`interior_rooms.ts RoomPlan`) fits
 * them as it stands. The server is the judge (`server/src/lib/indoor_combat_ops.ts` gathers the rows and calls this);
 * the client predicts with the same rule (`client/src/lib/indoor_combat_client.ts`: the reticle, the red mark, who is
 * drawn where). Change a rule here in BOTH files.
 *
 * ONE QUESTION: may a blow travel from A to T?  →  clear | blocked(why) | cover(height)
 *
 *   · Neither party is indoors (no live presence row names a building)        → clear, `indoors: false` — the street's
 *     own rule (the town footprint grid, `combat_geometry.lineOfSightClear`) judges exactly as before.
 *   · Two different buildings                                                → blocked 'building'.
 *   · One building, different FLOORS                                         → blocked 'floor' — except THE STAIRWELL:
 *     adjacent floors with both bodies inside the stair zone see each other through the slab's opening (the shell
 *     keeps that zone clear of slabs and furniture — interiors.md laws 22 / 38), so the run itself is never a refuge.
 *   · One building, one floor                                                → a 2D segment test in the building's
 *     frame against the partitions' SOLID spans (a doorway is a gap in them) → blocked 'wall', and against furniture
 *     that blocks sight (≥ 1.5 m, not see-through) → blocked 'furniture'; then, for a RANGED strike only, COVER: a
 *     piece ≥ COVER_MIN_M tall that the line crosses, wide enough across the line to hide a body (COVER_WIDTH_M),
 *     within COVER_NEAR_M of the TARGET, nearer the target than the attacker and beyond the attacker's own reach over
 *     it (COVER_LEAN_M) → cover(height). What cover DOES is the caller's (`coverEffect`): a crouched target is not
 *     hit (the shot is spent on the cover — blocked), a standing one takes the guard's halving. `at` is the WORLD
 *     point where the line meets the covering piece (where a spent shot is drawn landing).
 *   · Inside ↔ outside                                                       → blocked 'shell', except ALONG THE DOOR
 *     CORRIDOR: the inside party on the ground floor, the outside one in front of the door wall, and the line passing
 *     the door gap at the wall plane AND at the far end of the door's tunnel (the airtight sheet is 1.6 m thick) —
 *     then the ground floor's walls, furniture and cover as above. `door` is where the line leaves the tunnel: the
 *     street's rule judges the rest of the way.
 *   · THE LIFT SHAFT (slice L) is solid on every storey — two side walls, the shell behind it and a plug or the car:
 *     a line that meets its rect is blocked 'wall', and so is a blow to or from a body INSIDE it (a rider's height is
 *     the client's alone; the car is a closed box: nobody shoots out of it and nobody shoots into it).
 *
 * BOUNDED TRUST. A party is IN a building on a floor and in a room only when its presence row says so AND the server
 * judged that row against its own plan (`verified`). Anything else — an unverified row, a lapsed one, none at all, an
 * NPC's body — is where its 2D point is: inside the shell's core it stands "at the door": the GROUND FLOOR, no room
 * (never proof of shelter upstairs, never a free hit from a floor it cannot prove); outside the core it is outdoors.
 * A verified row's 2D point only lags the body, so it is clamped into the core and, where the room is known, into the
 * room it names.
 *
 * GEOMETRY IS OPTIONAL. `geom` is the room both sides are KNOWN to draw (the caller decides: the plan is the pinned
 * one, or every party's row was judged against it). Absent — a cold memo inside a tick, a drifted room — the floor
 * rule and the door rule stand alone: no wall, no furniture, no cover.
 */

/** The server's lever (platform_flag, ABSENT = ON): off → every strike is judged exactly as before this slice. */
export const INDOOR_COMBAT_FLAG = 'interior_combat_v1';

/** The half width of the door gap in the shell's −Z wall (m): `building_interiors.ts` DOOR_W / 2 + 0.1. */
export const DOOR_HALF_M = 1.05;
/** How far the doorway's tunnel reaches outward from the wall plane (m): the airtight sheet (T 1.6) less its inward overlap (0.125). */
export const DOOR_TUNNEL_M = 1.475;
/** How far the same sheets reach INWARD of the wall plane (m): the tunnel's inner mouth. */
export const DOOR_SILL_IN_M = 0.125;
/** The shell's inset from the footprint (m): `building_interiors.ts` WALL_IN — the core's half extent is min(w, 60) / 2 − this. */
export const SHELL_INSET_M = 0.45;
/** The widest core (m): `interiorSpec` clamps a monument to its 60 m heart. */
export const CORE_MAX_M = 60;
/** A storey (m): `building_interiors.ts` FLOOR_H. */
export const STOREY_M = 3.4;
/** A body's radius (m): a verified row's point is kept this far inside the core. */
export const BODY_R_M = 0.3;

/** Cover counts for a target within this of the piece (m) … */
export const COVER_NEAR_M = 1.5;
/** … when the piece is at least this tall (m) … */
export const COVER_MIN_M = 0.9;
/** … and at least this wide ACROSS the line of fire (m — two bodies' width: a chair's back hides nobody) … */
export const COVER_WIDTH_M = 1.2;
/** … and the attacker stands at least this far from it (m): within arm's reach of a counter you fire over it. */
export const COVER_LEAN_M = 1.0;
/** Furniture this tall blocks a line of sight outright (the planner's COVER_SIGHT_M — it sets `sight`). */
export const SIGHT_MIN_M = 1.5;
/** THE STAIRWELL: adjacent floors see each other inside the stair zone. `false` = floors are absolute. */
export const STAIR_SIGHT = true;
/** How far outside the stair run's rect a body still stands "on the stairs" (m). */
export const STAIR_PAD_M = 0.45;

export interface IcBox { x0: number; z0: number; x1: number; z1: number }
/** A partition's solid span. */
export type IcWall = IcBox;
/** A walk-blocking piece: `h` its cover height (m; 0 / absent = none), `sight` = it blocks a line of sight. */
export interface IcPiece extends IcBox { h?: number; sight?: boolean }
export interface IcRoom { id: string; rects: ReadonlyArray<IcBox> }
export interface IcFloor { walls: ReadonlyArray<IcWall>; pieces: ReadonlyArray<IcPiece>; rooms: ReadonlyArray<IcRoom> }

export interface IcBuilding {
  /** The presence id (`deed:<structureId>`). */
  id: string;
  /** The structure's frame: its row position and yaw (three's rotation.y). */
  x: number; z: number; yaw: number;
  /** The interior's half extents (the shell's core) and its storeys. */
  hw: number; hd: number; floors: number;
  /** The room every client draws, per floor — null when that is not known (the floor and door rules alone). */
  geom: ReadonlyArray<IcFloor | undefined> | null;
  /** The lift shaft's rect (building-local; the same on every storey) — judged only where `geom` is. */
  shaft?: IcBox | null;
}

export interface IcParty {
  /** WORLD position (the 2D row). */
  x: number; z: number;
  /** The building its LIVE presence row names ('' = none), with the row's floor and room. */
  building: string; floor: number; room: string;
  /** The row's room was judged against the server's plan. */
  verified: boolean;
}

export type IcBlock = 'building' | 'floor' | 'shell' | 'wall' | 'furniture';
export type IcRelation =
  | { kind: 'clear'; indoors: boolean; door?: { x: number; z: number } }
  | { kind: 'blocked'; why: IcBlock }
  | { kind: 'cover'; height: number; at: { x: number; z: number }; door?: { x: number; z: number } };

/** A party that is nowhere indoors (an NPC's body, a player with no live row). */
export function partyAt(x: number, z: number): IcParty {
  return { x, z, building: '', floor: 0, room: '', verified: false };
}

/** The core's half extent for a footprint's full width (what `interiorSpec` computes). */
export function coreHalf(fullWidthM: number): number {
  return Math.min(fullWidthM, CORE_MAX_M) / 2 - SHELL_INSET_M;
}
/** The storeys of a shell `h` metres tall (what `interiorSpec` computes). */
export function storeysOf(h: number): number {
  return Math.max(1, Math.min(8, Math.floor((h - 1.6) / STOREY_M)));
}

/** World → the building's frame (the grid's and the visit's own un-rotation). */
export function toLocal(b: { x: number; z: number; yaw: number }, x: number, z: number): { x: number; z: number } {
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  const dx = x - b.x, dz = z - b.z;
  return { x: dx * c - dz * s, z: dx * s + dz * c };
}
/** The building's frame → world. */
export function toWorld(b: { x: number; z: number; yaw: number }, lx: number, lz: number): { x: number; z: number } {
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  return { x: b.x + lx * c + lz * s, z: b.z - lx * s + lz * c };
}

/** The stair zone of a shell (building-local): the run along the −X wall and the slab opening over it. */
export function stairZone(hw: number, hd: number): IcBox {
  const d = Math.min(2 * hd - 2.4, STOREY_M * 1.9);
  return { x0: -hw + 1.1 - 0.8, x1: -hw + 1.1 + 0.8, z0: -d / 2, z1: d / 2 };
}

/** Where a segment P→Q is inside a box, as [t0, t1] of its length (null = it misses). The slab method. */
export function segBox(px: number, pz: number, qx: number, qz: number, b: IcBox): [number, number] | null {
  let t0 = 0, t1 = 1;
  const dx = qx - px, dz = qz - pz;
  if (Math.abs(dx) < 1e-12) { if (px < b.x0 || px > b.x1) return null; }
  else {
    let a = (b.x0 - px) / dx, c = (b.x1 - px) / dx;
    if (a > c) { const k = a; a = c; c = k; }
    if (a > t0) t0 = a;
    if (c < t1) t1 = c;
    if (t0 > t1) return null;
  }
  if (Math.abs(dz) < 1e-12) { if (pz < b.z0 || pz > b.z1) return null; }
  else {
    let a = (b.z0 - pz) / dz, c = (b.z1 - pz) / dz;
    if (a > c) { const k = a; a = c; c = k; }
    if (a > t0) t0 = a;
    if (c < t1) t1 = c;
    if (t0 > t1) return null;
  }
  return [t0, t1];
}
const inBox = (b: IcBox, x: number, z: number): boolean => x >= b.x0 && x <= b.x1 && z >= b.z0 && z <= b.z1;
/** A point's distance to a box (0 inside it). */
export function distToBox(b: IcBox, x: number, z: number): number {
  const dx = Math.max(b.x0 - x, 0, x - b.x1), dz = Math.max(b.z0 - z, 0, z - b.z1);
  return Math.hypot(dx, dz);
}
/** The nearest point of a room's rects to (x, z) — the point itself when it stands in one. */
export function snapIntoRoom(rects: ReadonlyArray<IcBox>, x: number, z: number): { x: number; z: number } {
  let best = { x, z }, bd = Infinity;
  for (const q of rects) {
    const cx = Math.min(q.x1, Math.max(q.x0, x)), cz = Math.min(q.z1, Math.max(q.z0, z));
    const d = (cx - x) * (cx - x) + (cz - z) * (cz - z);
    if (d < bd) { bd = d; best = { x: cx, z: cz }; }
    if (d === 0) break;
  }
  return best;
}

/** Where a party stands in a building's terms. */
export interface IcSeat {
  where: 'in' | 'out' | 'other';
  floor: number; room: string;
  /** Building-local metres. */
  lx: number; lz: number;
  /** The floor and room are the row's (verified); false = the ground floor by position alone. */
  sure: boolean;
}
export function seatOf(p: IcParty, b: IcBuilding): IcSeat {
  const l = toLocal(b, p.x, p.z);
  if (p.building !== '' && p.building !== b.id) return { where: 'other', floor: 0, room: '', lx: l.x, lz: l.z, sure: false };
  if (p.building === b.id && p.verified) {
    // the row is the judge of floor and room; the 2D point only lags the body
    const floor = Math.max(0, Math.min(Math.max(1, b.floors) - 1, Math.trunc(p.floor)));
    let lx = Math.min(b.hw - BODY_R_M, Math.max(-b.hw + BODY_R_M, l.x));
    let lz = Math.min(b.hd - BODY_R_M, Math.max(-b.hd + BODY_R_M, l.z));
    const room = b.geom?.[floor]?.rooms.find((r) => r.id === p.room);
    if (room && room.rects.length > 0) { const s = snapIntoRoom(room.rects, lx, lz); lx = s.x; lz = s.z; }
    return { where: 'in', floor, room: p.room, lx, lz, sure: true };
  }
  // no proof of a floor or a room: inside the core it stands on the GROUND FLOOR ("at the door"), else outdoors
  if (Math.abs(l.x) <= b.hw && Math.abs(l.z) <= b.hd) return { where: 'in', floor: 0, room: '', lx: l.x, lz: l.z, sure: false };
  return { where: 'out', floor: 0, room: '', lx: l.x, lz: l.z, sure: false };
}

const CLEAR_OUT: IcRelation = { kind: 'clear', indoors: false };
const blocked = (why: IcBlock): IcRelation => ({ kind: 'blocked', why });

/** One floor's verdict for a blow along P → Q (both building-local, on that floor). `coverForQ`: Q is the target and
 *  may be covered (a cover near the ATTACKER protects nobody); (ax, az) is where the attacker really stands (P itself,
 *  or — for a shot through the door — the point outside, which is farther than the door plane the segment starts at). */
function floorVerdict(px: number, pz: number, qx: number, qz: number, g: IcFloor | undefined, shaft: IcBox | null | undefined, ranged: boolean, coverForQ: boolean, ax = px, az = pz): IcRelation {
  if (!g) return { kind: 'clear', indoors: true };
  if (shaft && segBox(px, pz, qx, qz, shaft)) return blocked('wall');
  for (const w of g.walls) if (segBox(px, pz, qx, qz, w)) return blocked('wall');
  let cover = 0, atX = 0, atZ = 0;
  const len = Math.hypot(qx - px, qz - pz);
  const ux = len > 1e-9 ? (qx - px) / len : 0, uz = len > 1e-9 ? (qz - pz) / len : 0;
  for (const p of g.pieces) {
    // a piece a body stands ON (a bed, a bench) is not between anyone: it neither blocks nor covers
    if (inBox(p, px, pz) || inBox(p, qx, qz)) continue;
    const span = segBox(px, pz, qx, qz, p);
    if (!span) continue;
    if (p.sight) return blocked('furniture');
    const h = p.h ?? 0;
    if (!ranged || !coverForQ || h < COVER_MIN_M) continue;
    const across = (p.x1 - p.x0) * Math.abs(uz) + (p.z1 - p.z0) * Math.abs(ux);
    if (across < COVER_WIDTH_M) continue;
    const dT = distToBox(p, qx, qz), dA = distToBox(p, ax, az);
    if (dT > COVER_NEAR_M || dA <= dT || dA < COVER_LEAN_M) continue;
    if (h > cover) { cover = h; atX = px + (qx - px) * span[0]; atZ = pz + (qz - pz) * span[0]; }
  }
  // (`at` is building-local here; `indoorRelation` hands it out in world space)
  return cover > 0 ? { kind: 'cover', height: cover, at: { x: atX, z: atZ } } : { kind: 'clear', indoors: true };
}

/**
 * THE INDOOR RELATION. `a` attacks `t`; `b` is the building a row names (the caller resolves it; null = none could
 * be resolved → the street's rule). `ranged`: a gun (cover is a ranged matter; walls and floors stop everything).
 */
export function indoorRelation(a: IcParty, t: IcParty, b: IcBuilding | null, o: { ranged: boolean }): IcRelation {
  if (a.building === '' && t.building === '') return CLEAR_OUT;
  if (a.building !== '' && t.building !== '' && a.building !== t.building) return blocked('building');
  if (!b) return CLEAR_OUT;
  const A = seatOf(a, b), T = seatOf(t, b);
  if (A.where === 'other' || T.where === 'other') return blocked('building');
  if (A.where === 'out' && T.where === 'out') return CLEAR_OUT;
  if (A.where === 'in' && T.where === 'in') {
    if (A.floor !== T.floor) {
      if (STAIR_SIGHT && b.floors > 1 && Math.abs(A.floor - T.floor) === 1) {
        const s = stairZone(b.hw, b.hd);
        const z: IcBox = { x0: s.x0 - STAIR_PAD_M, x1: s.x1 + STAIR_PAD_M, z0: s.z0 - STAIR_PAD_M, z1: s.z1 + STAIR_PAD_M };
        if (inBox(z, A.lx, A.lz) && inBox(z, T.lx, T.lz)) return { kind: 'clear', indoors: true };
      }
      return blocked('floor');
    }
    const v = floorVerdict(A.lx, A.lz, T.lx, T.lz, b.geom?.[A.floor], b.shaft, o.ranged, true);
    return v.kind === 'cover' ? { kind: 'cover', height: v.height, at: toWorld(b, v.at.x, v.at.z) } : v;
  }
  // inside ↔ outside: the doorway is the shell's only aperture
  const I = A.where === 'in' ? A : T, O = A.where === 'in' ? T : A;
  if (I.floor !== 0) return blocked('shell');
  // The gap is a straight tunnel through the 1.6 m sheets, from its inner mouth (the sill) to its outer one: a line
  // passes it exactly when it is inside the gap at BOTH mouths (a body standing in the tunnel is its own mouth).
  const wallZ = -b.hd, innerZ = wallZ + DOOR_SILL_IN_M, outerZ = wallZ - DOOR_TUNNEL_M;
  if (!(O.lz < wallZ) || !(I.lz > wallZ)) return blocked('shell');
  const xAt = (z: number): number => I.lx + ((z - I.lz) / (O.lz - I.lz)) * (O.lx - I.lx);
  if (Math.abs(xAt(Math.min(I.lz, innerZ))) > DOOR_HALF_M) return blocked('shell');
  if (Math.abs(xAt(Math.max(O.lz, outerZ))) > DOOR_HALF_M) return blocked('shell');
  const x1 = xAt(wallZ);
  const door = O.lz < outerZ ? toWorld(b, xAt(outerZ), outerZ - 0.2) : undefined;
  // the ground floor between the door plane and the inside party; cover only where the INSIDE party is the target
  const insideIsTarget = T.where === 'in';
  const r = insideIsTarget
    ? floorVerdict(x1, wallZ, I.lx, I.lz, b.geom?.[0], b.shaft, o.ranged, true, O.lx, O.lz)
    : floorVerdict(I.lx, I.lz, x1, wallZ, b.geom?.[0], b.shaft, o.ranged, false);
  if (r.kind === 'blocked') return r;
  if (r.kind === 'cover') { const at = toWorld(b, r.at.x, r.at.z); return door ? { kind: 'cover', height: r.height, at, door } : { kind: 'cover', height: r.height, at }; }
  return door ? { kind: 'clear', indoors: true, door } : { kind: 'clear', indoors: true };
}

/** What a cover of `height` does to a ranged shot: a crouched target behind it is not hit (the shot is spent on the
 *  cover); a standing one takes the guard's halving; under COVER_MIN_M it is no cover. */
export function coverEffect(height: number, targetCrouched: boolean): 'none' | 'halve' | 'block' {
  if (!(height >= COVER_MIN_M)) return 'none';
  return targetCrouched ? 'block' : 'halve';
}

/** The words a refusal or a result carries for a blocked relation (the HUD's and the harness's vocabulary). */
export function blockWords(why: IcBlock): string {
  return why === 'building' ? 'another building' : why === 'floor' ? 'another floor' : why === 'shell' ? 'the wall' : why === 'wall' ? 'a partition' : 'furniture';
}
